/* eslint-disable @typescript-eslint/no-explicit-any */
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  AdminHeader,
  AdminSpinner,
  Box,
  BrandImageComponent,
  Button,
  Div,
  EditPenIcon,
  H5,
  HeaderIconWrapper,
  Icon,
  ListSpinner,
  P,
  PlusIcon,
  queryKeys,
  QueryStatus,
  SearchInput,
  TrashIconRed,
  getBrandTemplates,
  handleSetUrlSearchParams,
  restaurantImage,
} from '@vgrubs/components';
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { BrandTemplateEntity } from 'components/src/types/brand.types';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { BrandTemplateAdd } from './BrandTemplateAdd';
import { BrandTemplateDelete } from './BrandTemplateDelete';

export const ItemListImage = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;

  > img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  @media (max-width: 800px) {
    width: 50px;
    height: 50px;
  }
`;

export const fetchBrandTemplates = async ({
  pageParam = 1,
  query = '',
}: {
  pageParam: number;
  query: string;
}) => {
  const { data } = await getBrandTemplates({
    limit: 20,
    page: pageParam,
    query,
  });

  // If data length is less than the limit, we know there is no next page
  const nextPage = data.data.length === 20 ? pageParam + 1 : undefined;

  return {
    brandTemplates: data ?? [],
    nextPage,
  };
};

export const useInfiniteBrandTemplates = (query: string) =>
  useInfiniteQuery({
    initialPageParam: 1,
    queryKey: queryKeys.brandTemplates.list(query),
    queryFn: ({ pageParam = 1 }) => fetchBrandTemplates({ pageParam, query }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });

export const BrandTemplates = () => {
  const navigate = useNavigate();
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState<BrandTemplateEntity | null>(null);
  const [deleteModal, setDeleteModal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const observerRef = useRef<HTMLDivElement>(null);
  const initialQuery = searchParams.get('query') || '';
  const [inputValue, setInputValue] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => setQuery(value), 500),
    []
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    refetch,
  } = useInfiniteBrandTemplates(query);

  useEffect(() => {
    // Create a new intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        // If the target element is visible and there is more data to load
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage(); // Call the fetch for the next page
        }
      },
      {
        threshold: 1.0, // Trigger when 100% of the element is in view
      }
    );

    // Get the current target element (bottom observer element)
    const currentObserverTarget = observerRef.current;

    // Ensure that we have a valid target to observe
    if (currentObserverTarget) {
      observer.observe(currentObserverTarget); // Start observing
    }

    // Cleanup the observer on component unmount or before re-triggering
    return () => {
      if (currentObserverTarget) {
        observer.unobserve(currentObserverTarget); // Stop observing
      }
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]); // Re-run when these values change

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
    handleSetUrlSearchParams('query', value, setSearchParams);
  };

  return (
    <>
      <AdminHeader title={'Brand Templates'}>
        <Div $flex $gap="20px" $gapSM="20px">
          <HeaderIconWrapper onClick={() => setAddModal(true)}>
            <PlusIcon />
          </HeaderIconWrapper>
          <SearchInput
            value={inputValue}
            maxWidth="350px"
            maxWidthSM="150px"
            placeholder="Search"
            onChange={onSearchChange}
          />
        </Div>
      </AdminHeader>

      <Div $mt="32" $mtSM="20"></Div>

      {data?.pages.map((page) =>
        page.brandTemplates.data.map((brandTemplate, i) => {
          return (
            <Box
              key={i}
              $mb="8"
              $flex
              $alignCenter
              $between
              $gap="16px"
              $gapSM="8px"
            >
              <Div
                $flex
                $alignCenter
                $gap="20px"
                $gapSM="12px"
                onClick={() => setEditModal(brandTemplate)}
                $pointer
                $width="100%"
              >
                <BrandImageComponent
                  image={brandTemplate.imageUrl}
                  height={60}
                  width={60}
                />
                <div>
                  <H5 $mb="4">{brandTemplate.name}</H5>{' '}
                  <P $m="0">{brandTemplate.phoneNumber}</P>{' '}
                </div>
              </Div>
              <Div
                $flex
                $alignCenter
                $between
                $gap="16px"
                $gapSM="12px"
                $reverseSM
              >
                <Button
                  $selected
                  $small
                  $w="100px"
                  $wSM="84px"
                  $gray={!brandTemplate.isMenuSet}
                  onClick={() =>
                    navigate(`/templates-brand/menu/${brandTemplate.id}`)
                  }
                >
                  {brandTemplate.isMenuSet ? 'Edit Menu' : 'Set Menu'}
                </Button>
                <Div $flex $alignCenter $between $gap="16px">
                  <Icon
                    $shadow
                    $pointer
                    onClick={() => setEditModal(brandTemplate)}
                  >
                    <EditPenIcon />
                  </Icon>
                  <Icon
                    $pointer
                    onClick={() => setDeleteModal(brandTemplate.id)}
                  >
                    <TrashIconRed />
                  </Icon>
                </Div>
              </Div>
            </Box>
          );
        })
      )}

      {data?.pages[0]?.brandTemplates.data.length === 0 &&
        status !== QueryStatus.PENDING &&
        !isFetchingNextPage && (
          <NoDataComponent
            image={restaurantImage}
            title="No Brand Templates"
            description="No brand templates found"
          />
        )}

      <ListSpinner loading={isFetchingNextPage} observerRef={observerRef} />
      {(status === QueryStatus.PENDING || loading) && <AdminSpinner />}

      <BrandTemplateAdd
        modalIsOpen={addModal}
        closeModal={() => {
          setAddModal(false);
        }}
        setLoading={setLoading}
        refetch={refetch}
        type="add"
      />
      <BrandTemplateAdd
        modalIsOpen={editModal}
        closeModal={() => {
          setEditModal(null);
        }}
        setLoading={setLoading}
        refetch={refetch}
        type="edit"
      />

      <BrandTemplateDelete
        modalIsOpen={deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        setLoading={setLoading}
        refetch={refetch}
      />
    </>
  );
};
